import { Delete, Edit } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  InputAdornment,
  LinearProgress,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useNotify, usePermissions, useTranslate } from 'react-admin';
import { DashboardContent } from 'src/layouts/dashboard';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { Iconify } from 'src/components/iconify';
import { PermissionButton } from 'src/routes/components';
import type { PermissionData } from 'src/pages/admin/entities/permission.entity';
import { hasPermission } from 'src/pages/admin/utils/has-permission';

import { useLeverage } from './hooks/useLeverage';
import TierDialog from './components/TierDialog';
import type {
  LeverageCampaign,
  LeveragePrizePool,
  LeverageTier,
  LeverageTierPayload,
  TranslateFn,
} from './types';
import {
  computeCampaignStatus,
  formatUsdc,
  getCampaignStatusColor,
  getErrorMessage,
  getPoolPct,
  getRemainingUsdc,
  localInputToMs,
  msToLocalInput,
  normalizeNumberString,
} from './utils';

const I18N_PREFIX = 'module.boldPredictionCampaign';

const tL = (t: TranslateFn, key: string, options?: Record<string, unknown>) =>
  t(`${I18N_PREFIX}.${key}`, options);

type TabValue = 'c1' | 'c2';

export const MainPage = () => {
  const t = useTranslate();
  const notify = useNotify();

  const {
    campaign,
    pool,
    tiers,
    loading,
    saving,
    saveCampaign,
    upsertTier,
    deleteTier,
    setPoolTotal,
  } = useLeverage();

  const [tab, setTab] = useState<TabValue>('c1');

  return (
    <DashboardContent>
      <CustomBreadcrumbs
        heading={tL(t, 'configs')}
        links={[{ name: tL(t, 'title') }, { name: tL(t, 'configs') }]}
        sx={{ mb: 3 }}
      />

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {tL(t, 'description')}
      </Typography>

      <Tabs value={tab} onChange={(_, value) => setTab(value)} sx={{ mb: 3 }}>
        <Tab value="c1" label={tL(t, 'tab.c1')} />
        <Tab value="c2" label={tL(t, 'tab.c2')} />
      </Tabs>

      {loading ? (
        <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 240 }}>
          <CircularProgress size={28} />
        </Stack>
      ) : tab === 'c1' ? (
        <C1Panel
          campaign={campaign}
          saving={saving}
          onSave={async (payload) => {
            try {
              await saveCampaign(payload);
              notify(tL(t, 'notify.saveSuccess'), { type: 'success' });
            } catch (err) {
              notify(getErrorMessage(err) || tL(t, 'notify.saveFailed'), { type: 'error' });
            }
          }}
          t={t}
        />
      ) : (
        <C2Panel
          campaign={campaign}
          pool={pool}
          tiers={tiers}
          saving={saving}
          onSaveCampaign={async (payload) => {
            try {
              await saveCampaign(payload);
              notify(tL(t, 'notify.saveSuccess'), { type: 'success' });
            } catch (err) {
              notify(getErrorMessage(err) || tL(t, 'notify.saveFailed'), { type: 'error' });
            }
          }}
          onSavePoolTotal={async (totalUsdc) => {
            try {
              await setPoolTotal({ totalUsdc });
              notify(tL(t, 'notify.saveSuccess'), { type: 'success' });
            } catch (err) {
              notify(getErrorMessage(err) || tL(t, 'notify.saveFailed'), { type: 'error' });
            }
          }}
          onUpsertTier={async (payload) => {
            try {
              await upsertTier(payload);
              notify(tL(t, 'notify.saveSuccess'), { type: 'success' });
            } catch (err) {
              notify(getErrorMessage(err) || tL(t, 'notify.saveFailed'), { type: 'error' });
            }
          }}
          onDeleteTier={async (tierIndex) => {
            if (!window.confirm(tL(t, 'tier.confirmDelete'))) return;
            try {
              await deleteTier(tierIndex);
              notify(tL(t, 'notify.deleteSuccess'), { type: 'success' });
            } catch (err) {
              notify(getErrorMessage(err) || tL(t, 'notify.deleteFailed'), { type: 'error' });
            }
          }}
          t={t}
        />
      )}
    </DashboardContent>
  );
};

// ============ Tab 1: First Match No-Worry ============

const C1Panel = memo(
  ({
    campaign,
    saving,
    onSave,
    t,
  }: {
    campaign: LeverageCampaign;
    saving: boolean;
    onSave: (payload: Partial<LeverageCampaign>) => void;
    t: TranslateFn;
  }) => {
    const { permissions } = usePermissions<PermissionData>();
    const canWrite = hasPermission(permissions?.permissions, 'world-cup-leverage', 'write');

    const [enabled, setEnabled] = useState(false);
    const [startInput, setStartInput] = useState('');
    const [endInput, setEndInput] = useState('');
    const [multiplier, setMultiplier] = useState('');
    const [voucherCap, setVoucherCap] = useState('');

    useEffect(() => {
      setEnabled(Boolean(campaign.c1IsEnabled));
      setStartInput(msToLocalInput(campaign.c1StartAt));
      setEndInput(msToLocalInput(campaign.c1EndAt));
      setMultiplier(String(campaign.multiplier ?? ''));
      setVoucherCap(String(campaign.voucherCap ?? ''));
    }, [campaign]);

    const isDirty =
      enabled !== Boolean(campaign.c1IsEnabled) ||
      localInputToMs(startInput) !== campaign.c1StartAt ||
      localInputToMs(endInput) !== campaign.c1EndAt ||
      Number(multiplier) !== Number(campaign.multiplier) ||
      Number(voucherCap) !== Number(campaign.voucherCap);

    const multiplierNum = Number(multiplier);
    const voucherCapNum = Number(voucherCap);
    const startMs = localInputToMs(startInput);
    const endMs = localInputToMs(endInput);

    const isInvalid =
      !startMs ||
      !endMs ||
      endMs <= startMs ||
      !Number.isFinite(multiplierNum) ||
      multiplierNum <= 0 ||
      !Number.isFinite(voucherCapNum) ||
      voucherCapNum <= 0;

    const handleReset = () => {
      setEnabled(Boolean(campaign.c1IsEnabled));
      setStartInput(msToLocalInput(campaign.c1StartAt));
      setEndInput(msToLocalInput(campaign.c1EndAt));
      setMultiplier(String(campaign.multiplier ?? ''));
      setVoucherCap(String(campaign.voucherCap ?? ''));
    };

    const handleSave = () => {
      onSave({
        ...campaign,
        c1IsEnabled: enabled,
        c1StartAt: startMs,
        c1EndAt: endMs,
        multiplier: multiplierNum,
        voucherCap: voucherCapNum,
      });
    };

    return (
      <Stack spacing={3}>
        <Card sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {tL(t, 'section.basic')}
          </Typography>
          <Stack spacing={2}>
            <FieldRow
              label={tL(t, 'field.status')}
              // hint={tL(t, 'field.statusHint')}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={enabled}
                    onChange={(_, v) => setEnabled(v)}
                    color="success"
                    disabled={!canWrite}
                  />
                }
                label={(() => {
                  const status = computeCampaignStatus(enabled, startMs, endMs);
                  return (
                    <Chip
                      label={tL(t, `status.${status}`)}
                      color={getCampaignStatusColor(status)}
                      size="small"
                    />
                  );
                })()}
              />
            </FieldRow>
            <FieldRow label={tL(t, 'field.startAt')}>
              <TextField
                type="datetime-local"
                size="small"
                value={startInput}
                onChange={(e) => setStartInput(e.target.value)}
                error={!startMs}
                helperText={!startMs ? t('ra.validation.required') : undefined}
              />
            </FieldRow>
            <FieldRow label={tL(t, 'field.endAt')}>
              <TextField
                type="datetime-local"
                size="small"
                value={endInput}
                onChange={(e) => setEndInput(e.target.value)}
                error={!endMs || (Boolean(startMs) && endMs <= startMs)}
                helperText={
                  !endMs
                    ? t('ra.validation.required')
                    : startMs && endMs <= startMs
                      ? tL(t, 'error.endBeforeStart')
                      : undefined
                }
              />
            </FieldRow>
            <FieldRow label={tL(t, 'field.multiplier')} hint={tL(t, 'field.multiplierHint')}>
              <TextField
                size="small"
                value={multiplier}
                onChange={(e) => setMultiplier(e.target.value.replace(/[^\d.]/g, ''))}
                error={!Number.isFinite(multiplierNum) || multiplierNum <= 0}
                helperText={
                  !Number.isFinite(multiplierNum) || multiplierNum <= 0
                    ? t('ra.validation.required')
                    : undefined
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">{tL(t, 'unit.multiplier')}</InputAdornment>
                  ),
                }}
                inputProps={{ inputMode: 'decimal' }}
              />
            </FieldRow>
            <FieldRow label={tL(t, 'field.voucherCap')} hint={tL(t, 'field.voucherCapHint')}>
              <TextField
                size="small"
                value={voucherCap}
                onChange={(e) => setVoucherCap(e.target.value.replace(/[^\d]/g, ''))}
                error={!Number.isFinite(voucherCapNum) || voucherCapNum <= 0}
                helperText={
                  !Number.isFinite(voucherCapNum) || voucherCapNum <= 0
                    ? t('ra.validation.required')
                    : undefined
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">{tL(t, 'unit.voucher')}</InputAdornment>
                  ),
                }}
                inputProps={{ inputMode: 'numeric' }}
              />
            </FieldRow>
          </Stack>
        </Card>

        <Stack direction="row" justifyContent="flex-end" spacing={1.5}>
          <Button variant="outlined" onClick={handleReset} disabled={saving || !isDirty}>
            {tL(t, 'actions.reset')}
          </Button>
          <PermissionButton resource="world-cup-leverage" action="write">
            <LoadingButton
              variant="contained"
              loading={saving}
              disabled={!isDirty || isInvalid}
              onClick={handleSave}
            >
              {tL(t, 'actions.save')}
            </LoadingButton>
          </PermissionButton>
        </Stack>
      </Stack>
    );
  }
);

C1Panel.displayName = 'C1Panel';

// ============ Tab 2: Team Strike ============

const C2Panel = memo(
  ({
    campaign,
    pool,
    tiers,
    saving,
    onSaveCampaign,
    onSavePoolTotal,
    onUpsertTier,
    onDeleteTier,
    t,
  }: {
    campaign: LeverageCampaign;
    pool: LeveragePrizePool;
    tiers: LeverageTier[];
    saving: boolean;
    onSaveCampaign: (payload: Partial<LeverageCampaign>) => void;
    onSavePoolTotal: (totalUsdc: string) => void;
    onUpsertTier: (payload: LeverageTierPayload) => void;
    onDeleteTier: (tierIndex: number) => void;
    t: TranslateFn;
  }) => {
    const { permissions } = usePermissions<PermissionData>();
    const canWrite = hasPermission(permissions?.permissions, 'world-cup-leverage', 'write');

    const [enabled, setEnabled] = useState(false);
    const [startInput, setStartInput] = useState('');
    const [endInput, setEndInput] = useState('');
    const [totalInput, setTotalInput] = useState('');

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingTier, setEditingTier] = useState<LeverageTier | null>(null);

    useEffect(() => {
      setEnabled(Boolean(campaign.c2IsEnabled));
      setStartInput(msToLocalInput(campaign.c2StartAt));
      setEndInput(msToLocalInput(campaign.c2EndAt));
    }, [campaign]);

    useEffect(() => {
      setTotalInput(String(pool.totalUsdc ?? '0'));
    }, [pool]);

    const startMs = localInputToMs(startInput);
    const endMs = localInputToMs(endInput);
    const pct = getPoolPct(pool);
    const remaining = getRemainingUsdc(pool);

    const isCampaignDirty =
      enabled !== Boolean(campaign.c2IsEnabled) ||
      startMs !== campaign.c2StartAt ||
      endMs !== campaign.c2EndAt;
    const isCampaignInvalid = !startMs || !endMs || endMs <= startMs;

    const totalNum = Number(totalInput);
    const isTotalDirty = String(totalInput) !== String(pool.totalUsdc);

    const handleOpenAdd = () => {
      setEditingTier(null);
      setDialogOpen(true);
    };

    const handleOpenEdit = (tier: LeverageTier) => {
      setEditingTier(tier);
      setDialogOpen(true);
    };

    const validation = useMemo(() => {
      const indexes = tiers.map((tt) => tt.tierIndex);
      const hasDuplicate = new Set(indexes).size !== indexes.length;
      const enabledCount = tiers.length;
      const totalReward = tiers.reduce((sum, tt) => sum + (Number(tt.rewardUsdc) || 0), 0);
      return { hasDuplicate, enabledCount, totalReward };
    }, [tiers]);

    return (
      <Stack spacing={3}>
        <Card sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {tL(t, 'section.basic')}
          </Typography>
          <Stack spacing={2}>
            <FieldRow
              label={tL(t, 'field.status')}
              // hint={tL(t, 'field.statusHint')}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={enabled}
                    onChange={(_, v) => setEnabled(v)}
                    color="success"
                    disabled={!canWrite}
                  />
                }
                label={(() => {
                  const status = computeCampaignStatus(enabled, startMs, endMs);
                  return (
                    <Chip
                      label={tL(t, `status.${status}`)}
                      color={getCampaignStatusColor(status)}
                      size="small"
                    />
                  );
                })()}
              />
            </FieldRow>
            <FieldRow label={tL(t, 'field.startAt')}>
              <TextField
                type="datetime-local"
                size="small"
                value={startInput}
                onChange={(e) => setStartInput(e.target.value)}
              />
            </FieldRow>
            <FieldRow label={tL(t, 'field.endAt')}>
              <TextField
                type="datetime-local"
                size="small"
                value={endInput}
                onChange={(e) => setEndInput(e.target.value)}
                error={Boolean(startMs) && Boolean(endMs) && endMs <= startMs}
                helperText={
                  startMs && endMs && endMs <= startMs ? tL(t, 'error.endBeforeStart') : undefined
                }
              />
            </FieldRow>
            <Stack direction="row" justifyContent="flex-end">
              <PermissionButton resource="world-cup-leverage" action="write">
                <LoadingButton
                  variant="contained"
                  size="small"
                  loading={saving}
                  disabled={!isCampaignDirty || isCampaignInvalid}
                  onClick={() =>
                    onSaveCampaign({
                      ...campaign,
                      c2IsEnabled: enabled,
                      c2StartAt: startMs,
                      c2EndAt: endMs,
                    })
                  }
                >
                  {tL(t, 'actions.saveBasic')}
                </LoadingButton>
              </PermissionButton>
            </Stack>
          </Stack>
        </Card>

        <Card sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {tL(t, 'section.pool')}
          </Typography>
          <Stack spacing={2}>
            <FieldRow label={tL(t, 'pool.total')} hint={tL(t, 'pool.totalHint')}>
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                  size="small"
                  value={totalInput}
                  onChange={(e) => setTotalInput(e.target.value.replace(/[^\d.]/g, ''))}
                  error={!Number.isFinite(totalNum) || totalNum < 0}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">USDC</InputAdornment>,
                  }}
                  inputProps={{ inputMode: 'decimal' }}
                />
                <PermissionButton resource="world-cup-leverage" action="write">
                  <LoadingButton
                    variant="outlined"
                    size="small"
                    loading={saving}
                    disabled={!isTotalDirty || !Number.isFinite(totalNum) || totalNum < 0}
                    onClick={() => onSavePoolTotal(normalizeNumberString(totalInput))}
                  >
                    {tL(t, 'actions.save')}
                  </LoadingButton>
                </PermissionButton>
              </Stack>
            </FieldRow>

            <FieldRow label={tL(t, 'pool.consumed')} hint={tL(t, 'pool.consumedHint')}>
              <Stack spacing={1}>
                <Typography variant="h6">{formatUsdc(pool.consumedUsdc)} USDC</Typography>
                <LinearProgress
                  variant="determinate"
                  value={pct}
                  sx={{ height: 8, borderRadius: 999 }}
                  color="primary"
                />
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="caption" color="text.secondary">
                    {tL(t, 'pool.consumedLabel', {
                      consumed: formatUsdc(pool.consumedUsdc),
                      pct: pct ? (pct >= 1 ? pct.toFixed(0) : pct.toFixed(1)) : '0',
                    })}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {tL(t, 'pool.remaining', { remaining: formatUsdc(remaining) })}
                  </Typography>
                </Stack>
              </Stack>
            </FieldRow>
          </Stack>
        </Card>

        <Card sx={{ p: 3 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h6">{tL(t, 'section.tiers')}</Typography>
            <Button
              variant="outlined"
              startIcon={<Iconify icon="eva:plus-fill" />}
              onClick={handleOpenAdd}
            >
              {tL(t, 'tier.add')}
            </Button>
          </Stack>

          <Alert
            severity={validation.hasDuplicate ? 'error' : 'success'}
            icon={<Iconify icon="eva:checkmark-circle-2-fill" />}
            sx={{ mb: 2 }}
          >
            {validation.hasDuplicate
              ? tL(t, 'tier.validation.duplicate')
              : tL(t, 'tier.validation.ok', {
                  count: validation.enabledCount,
                  total: formatUsdc(validation.totalReward),
                })}
          </Alert>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 80 }}>{tL(t, 'tier.col.tierIndex')}</TableCell>
                  <TableCell>{tL(t, 'tier.col.threshold')}</TableCell>
                  <TableCell>{tL(t, 'tier.col.reward')}</TableCell>
                  <TableCell align="right" sx={{ width: 120 }}>
                    {tL(t, 'tier.col.action')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tiers.length ? (
                  tiers.map((tier) => (
                    <TableRow key={tier.tierIndex} hover>
                      <TableCell>{tier.tierIndex}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {formatUsdc(tier.volumeThresholdUsdc)} USDC
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {formatUsdc(tier.rewardUsdc)} USDC
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <IconButton size="small" onClick={() => handleOpenEdit(tier)}>
                            <Edit fontSize="small" />
                          </IconButton>
                          <PermissionButton resource="world-cup-leverage" action="write">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => onDeleteTier(tier.tierIndex)}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </PermissionButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Stack alignItems="center" sx={{ py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          {tL(t, 'tier.empty')}
                        </Typography>
                      </Stack>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        <TierDialog
          open={dialogOpen}
          saving={saving}
          editing={editingTier}
          tiers={tiers}
          onClose={() => setDialogOpen(false)}
          onSubmit={async (payload) => {
            await onUpsertTier(payload);
            setDialogOpen(false);
          }}
        />
      </Stack>
    );
  }
);

C2Panel.displayName = 'C2Panel';

const FieldRow = memo(
  ({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) => (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '250px 1fr' },
        gap: 2,
        alignItems: 'flex-start',
      }}
    >
      <Box>
        <Typography variant="body2" fontWeight={600}>
          {label}
        </Typography>
        {hint ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {hint}
          </Typography>
        ) : null}
      </Box>
      <Box>{children}</Box>
    </Box>
  )
);

FieldRow.displayName = 'FieldRow';

export default MainPage;
